import React, { useMemo, useState, useCallback } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  Switch,
  FormControlLabel,
  Tooltip,
  Collapse,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import PendingIcon from '@mui/icons-material/Pending';
import BlockIcon from '@mui/icons-material/Block';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { PlanStep, PlanPhase, StepStatus, StepKind, Participant, PlanRole } from '../../../types/generated/graphql';
import { colors } from '../../../theme/colors';
import GravatarAvatar from '../../GravatarAvatar';
import { resolveStepAssignee, ResolvedAssignment } from '../../../utils/planHelpers';
import AutomationBadge from '../../shared/AutomationBadge';

/** Resolved dependency with step info and status */
interface ResolvedDependency {
  id: string;
  title: string;
  status: StepStatus;
  isCompleted: boolean;
}

/**
 * Resolve dependency IDs to step objects with status info.
 * Handles missing IDs gracefully.
 */
function resolveDependencies(
  step: PlanStep,
  allSteps: PlanStep[]
): ResolvedDependency[] {
  if (!step.dependsOn || step.dependsOn.length === 0) {
    return [];
  }

  const stepMap = new Map(allSteps.map((s) => [s.id, s]));
  const resolved: ResolvedDependency[] = [];

  for (const depId of step.dependsOn) {
    const depStep = stepMap.get(depId);
    if (depStep) {
      resolved.push({
        id: depStep.id,
        title: depStep.title,
        status: depStep.status,
        isCompleted: depStep.status === StepStatus.Completed,
      });
    }
  }

  return resolved;
}

interface StepListPaneProps {
  steps: PlanStep[];
  phases: PlanPhase[];
  participants: Participant[];
  roles?: PlanRole[];
  selectedStepId: string | null;
  onSelectStep: (stepId: string) => void;
}

// Get status indicator icon and color
const getStatusIndicator = (status: StepStatus) => {
  switch (status) {
    case StepStatus.Completed:
      return { Icon: CheckCircleIcon, color: colors.green[600] };
    case StepStatus.InProgress:
      return { Icon: PendingIcon, color: colors.blue[600] };
    case StepStatus.WaitingExternal:
      return { Icon: PendingIcon, color: colors.amber[600] };
    case StepStatus.Blocked:
      return { Icon: BlockIcon, color: colors.red[600] };
    case StepStatus.Skipped:
      return { Icon: RemoveCircleOutlineIcon, color: colors.gray[400] };
    default:
      return { Icon: RadioButtonUncheckedIcon, color: colors.gray[400] };
  }
};

export const StepListPane: React.FC<StepListPaneProps> = ({
  steps,
  phases,
  participants,
  roles = [],
  selectedStepId,
  onSelectStep,
}) => {
  // Helper to resolve step assignment using role-based logic
  const getAssignment = useCallback(
    (step: PlanStep): ResolvedAssignment => {
      return resolveStepAssignee(step, roles, participants);
    },
    [roles, participants]
  );
  // Toggle for showing/hiding pruned (skipped) steps
  const [showPrunedSteps, setShowPrunedSteps] = useState(true);

  // Track expanded state for dependency sections (by step ID)
  // Completed steps default to collapsed, others to expanded
  const [expandedDeps, setExpandedDeps] = useState<Set<string>>(new Set());

  // Toggle dependency section expansion
  const toggleDepsExpanded = useCallback((stepId: string) => {
    setExpandedDeps((prev) => {
      const next = new Set(prev);
      if (next.has(stepId)) {
        next.delete(stepId);
      } else {
        next.add(stepId);
      }
      return next;
    });
  }, []);

  // Get selected step and its dependencies
  const selectedStep = useMemo(
    () => (selectedStepId ? steps.find((s) => s.id === selectedStepId) : null),
    [steps, selectedStepId]
  );

  const selectedStepDependencies = useMemo(
    () => (selectedStep ? resolveDependencies(selectedStep, steps) : []),
    [selectedStep, steps]
  );

  // Determine if deps section should be expanded
  // Default: expanded for non-completed steps, collapsed for completed
  const isDepsExpanded = useMemo(() => {
    if (!selectedStep || selectedStepDependencies.length === 0) return false;
    const isCompleted = selectedStep.status === StepStatus.Completed;
    // If user has explicitly toggled, use that state
    if (expandedDeps.has(selectedStep.id)) {
      return !isCompleted; // Toggle inverts the default
    }
    // Default state based on completion
    return !isCompleted;
  }, [selectedStep, selectedStepDependencies, expandedDeps]);

  // Count pruned steps
  const prunedStepCount = useMemo(
    () => steps.filter((s) => s.status === StepStatus.Skipped).length,
    [steps]
  );

  // Filter steps based on toggle
  const visibleSteps = useMemo(
    () => (showPrunedSteps ? steps : steps.filter((s) => s.status !== StepStatus.Skipped)),
    [steps, showPrunedSteps]
  );

  // Group steps by phase, sorted by phase order
  const stepsByPhase = useMemo(() => {
    const byPhase = new Map<string, PlanStep[]>();

    // Initialize with phase IDs in order
    for (const phase of phases) {
      byPhase.set(phase.id, []);
    }
    byPhase.set('unassigned', []);

    // Include visible steps (respects showPrunedSteps toggle)
    for (const step of visibleSteps) {
      const phaseId = step.phaseId || 'unassigned';
      const bucket = byPhase.get(phaseId) || [];
      bucket.push(step);
      byPhase.set(phaseId, bucket);
    }

    return byPhase;
  }, [visibleSteps, phases]);

  // Calculate global step number
  const getGlobalStepNumber = (step: PlanStep): number => {
    let count = 0;
    for (const phase of phases) {
      const phaseSteps = stepsByPhase.get(phase.id) || [];
      for (const s of phaseSteps) {
        count++;
        if (s.id === step.id) return count;
      }
    }
    // Check unassigned
    const unassigned = stepsByPhase.get('unassigned') || [];
    for (const s of unassigned) {
      count++;
      if (s.id === step.id) return count;
    }
    return count;
  };

  // Render dependency sub-section for the selected step
  const renderDependencySection = () => {
    // Get backend-computed dependency status
    const depStatus = selectedStep?.dependencyStatus;
    const hiddenBlockers = depStatus?.blockedByHiddenSteps || 0;
    const hasVisibleDeps = selectedStepDependencies.length > 0;
    const hasHiddenBlockers = hiddenBlockers > 0;

    // Show section if there are visible dependencies OR hidden blockers
    if (!selectedStep || (!hasVisibleDeps && !hasHiddenBlockers)) return null;

    const completedCount = selectedStepDependencies.filter((d) => d.isCompleted).length;
    const totalBlockers = selectedStepDependencies.length + hiddenBlockers;
    const totalCompleted = completedCount; // Hidden blockers are by definition incomplete

    return (
      <Box
        sx={{
          ml: 4,
          mr: 1,
          mb: 1,
          pl: 1.5,
          borderLeft: `2px solid ${hasHiddenBlockers ? colors.orange[600] : colors.gray[200]}`,
        }}
      >
        {/* Header - clickable to toggle */}
        <Box
          onClick={() => toggleDepsExpanded(selectedStep.id)}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            py: 0.5,
            cursor: 'pointer',
            '&:hover': { bgcolor: colors.gray[50] },
            borderRadius: 0.5,
            px: 0.5,
            mx: -0.5,
          }}
        >
          {isDepsExpanded ? (
            <ExpandMoreIcon sx={{ fontSize: 14, color: colors.gray[400] }} />
          ) : (
            <ChevronRightIcon sx={{ fontSize: 14, color: colors.gray[400] }} />
          )}
          <Typography
            variant="caption"
            sx={{
              color: hasHiddenBlockers ? colors.orange[600] : colors.gray[500],
              fontWeight: 500,
              fontSize: '0.7rem',
            }}
          >
            {hasHiddenBlockers ? 'Blocked' : 'Depends on'}
          </Typography>
          {!isDepsExpanded && (
            <Typography
              variant="caption"
              sx={{
                color: hasHiddenBlockers ? colors.orange[600] : colors.gray[400],
                fontSize: '0.65rem',
              }}
            >
              ({totalCompleted}/{totalBlockers})
            </Typography>
          )}
        </Box>

        {/* Dependency list */}
        <Collapse in={isDepsExpanded}>
          <Box sx={{ pt: 0.25 }}>
            {/* Show hidden blockers notice first */}
            {hasHiddenBlockers && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  py: 0.5,
                  px: 0.5,
                  mx: -0.5,
                  borderRadius: 0.5,
                  bgcolor: colors.orange[50],
                }}
              >
                <VisibilityOffIcon sx={{ fontSize: 12, color: colors.orange[600] }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.orange[700],
                    fontSize: '0.75rem',
                    fontStyle: 'italic',
                  }}
                >
                  {hiddenBlockers} other task{hiddenBlockers > 1 ? 's' : ''} must complete first
                </Typography>
              </Box>
            )}
            {/* Visible dependencies */}
            {selectedStepDependencies.map((dep) => (
              <Box
                key={dep.id}
                onClick={() => onSelectStep(dep.id)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.75,
                  py: 0.5,
                  px: 0.5,
                  mx: -0.5,
                  borderRadius: 0.5,
                  cursor: 'pointer',
                  '&:hover': { bgcolor: colors.gray[50] },
                }}
              >
                {dep.isCompleted ? (
                  <CheckCircleIcon sx={{ fontSize: 12, color: colors.green[600] }} />
                ) : (
                  <RadioButtonUncheckedIcon sx={{ fontSize: 12, color: colors.gray[400] }} />
                )}
                <Typography
                  variant="caption"
                  sx={{
                    color: dep.isCompleted ? colors.gray[500] : colors.gray[600],
                    fontSize: '0.75rem',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textDecoration: dep.isCompleted ? 'line-through' : 'none',
                  }}
                >
                  {dep.title}
                </Typography>
              </Box>
            ))}
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Render a step row
  const renderStepRow = (step: PlanStep) => {
    const isSelected = step.id === selectedStepId;
    const isCompleted = step.status === StepStatus.Completed;
    const isSkipped = step.status === StepStatus.Skipped;
    const { Icon, color } = getStatusIndicator(step.status);
    const stepNumber = getGlobalStepNumber(step);
    const assignment = getAssignment(step);
    const hasDependencies = isSelected && selectedStepDependencies.length > 0;

    return (
      <React.Fragment key={step.id}>
        <ListItemButton
          selected={isSelected}
          onClick={() => onSelectStep(step.id)}
          sx={{
            py: 1.5,
            px: 2,
            borderRadius: 1,
            mb: hasDependencies ? 0 : 0.5,
            transition: 'all 0.15s',
            opacity: isSkipped ? 0.5 : 1,
            '&.Mui-selected': {
              // Reduce saturation for completed steps - more subtle selection
              bgcolor: isCompleted ? `${colors.gray[500]}0A` : `${colors.blue[500]}14`,
              '&:hover': {
                bgcolor: isCompleted ? `${colors.gray[500]}12` : `${colors.blue[500]}20`,
              },
            },
            '&:hover': {
              bgcolor: colors.gray[50],
            },
          }}
        >
          {/* Step number */}
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 1.5,
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 18, color }} />
          </Box>

          {/* Step number and title */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: isSelected ? 600 : 500,
                color: isCompleted || isSkipped ? colors.textSecondary : colors.textPrimary,
                textDecoration: isCompleted || isSkipped ? 'line-through' : 'none',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              <Box component="span" sx={{ color: colors.textSecondary, mr: 0.75 }}>
                {stepNumber}.
              </Box>
              {step.title}
              {isSkipped && (
                <Tooltip
                  title={
                    step.applicability?.prunedTrigger?.description ||
                    step.applicability?.reason ||
                    'This step is not needed based on your decisions'
                  }
                  placement="right"
                >
                  <Box
                    component="span"
                    sx={{
                      ml: 1,
                      fontSize: '0.7rem',
                      color: colors.textSecondary,
                      fontStyle: 'italic',
                      cursor: 'help',
                    }}
                  >
                    (not needed)
                  </Box>
                </Tooltip>
              )}
            </Typography>
          </Box>

          {/* Automation badge - show for AUTOMATION and MIXED steps */}
          {(step.kind === StepKind.Automation || step.kind === StepKind.Mixed) && (
            <Box sx={{ flexShrink: 0, ml: 1 }}>
              <AutomationBadge
                kind={step.kind}
                hasActions={Boolean(step.actions?.length)}
                hasSkills={Boolean(step.skills?.length)}
                size="small"
              />
            </Box>
          )}

          {/* Assignee avatar or role indicator */}
          {(assignment.participant || assignment.role) && (
            <Box sx={{ flexShrink: 0, ml: 1 }}>
              {assignment.participant ? (
                <Tooltip
                  title={
                    assignment.source === 'role' && assignment.role
                      ? `${assignment.participant.name} (${assignment.role.name})`
                      : `Assigned to ${assignment.participant.name}`
                  }
                >
                  <Box>
                    <GravatarAvatar
                      email={assignment.participant.email}
                      name={assignment.participant.name}
                      size={22}
                      showTooltip={false}
                    />
                  </Box>
                </Tooltip>
              ) : assignment.role ? (
                <Tooltip title={`${assignment.role.name} (unassigned)`}>
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      bgcolor: colors.amber[100],
                      color: colors.amber[700],
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '0.65rem',
                      fontWeight: 600,
                      border: `1px dashed ${colors.amber[400]}`,
                    }}
                  >
                    {assignment.role.name.charAt(0).toUpperCase()}
                  </Box>
                </Tooltip>
              ) : null}
            </Box>
          )}
        </ListItemButton>

        {/* Dependency sub-section - shown under selected step */}
        {isSelected && renderDependencySection()}
      </React.Fragment>
    );
  };

  return (
    <Box
      sx={{
        bgcolor: colors.card,
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${colors.border}`,
          bgcolor: colors.gray[50],
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: colors.textPrimary }}>
          Guided Steps
        </Typography>

        {/* Show pruned steps toggle - only show if there are pruned steps */}
        {prunedStepCount > 0 && (
          <Tooltip
            title={
              showPrunedSteps
                ? `Hide ${prunedStepCount} pruned step${prunedStepCount !== 1 ? 's' : ''}`
                : `Show ${prunedStepCount} pruned step${prunedStepCount !== 1 ? 's' : ''}`
            }
          >
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={showPrunedSteps}
                  onChange={(e) => setShowPrunedSteps(e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': {
                      color: colors.gray[500],
                    },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: colors.gray[400],
                    },
                  }}
                />
              }
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VisibilityOffIcon sx={{ fontSize: 14, color: colors.gray[500] }} />
                  <Typography
                    variant="caption"
                    sx={{ color: colors.textSecondary, fontSize: '0.7rem' }}
                  >
                    {prunedStepCount}
                  </Typography>
                </Box>
              }
              labelPlacement="start"
              sx={{
                m: 0,
                '& .MuiFormControlLabel-label': {
                  mr: 0.5,
                },
              }}
            />
          </Tooltip>
        )}
      </Box>

      {/* Step list - auto height, no scroll */}
      <Box sx={{ p: 1 }}>
        {phases.map((phase) => {
          const phaseSteps = stepsByPhase.get(phase.id) || [];
          if (phaseSteps.length === 0) return null;

          return (
            <Box key={phase.id} sx={{ mb: 2 }}>
              {/* Phase label */}
              <Box sx={{ px: 1, py: 0.75, mb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.65rem',
                  }}
                >
                  {phase.title}
                </Typography>
              </Box>

              {/* Steps in phase */}
              <List disablePadding>
                {phaseSteps.map(renderStepRow)}
              </List>
            </Box>
          );
        })}

        {/* Unassigned steps */}
        {(() => {
          const unassigned = stepsByPhase.get('unassigned') || [];
          if (unassigned.length === 0) return null;

          return (
            <Box sx={{ mb: 2 }}>
              <Box sx={{ px: 1, py: 0.75, mb: 0.5 }}>
                <Typography
                  variant="caption"
                  sx={{
                    fontWeight: 600,
                    color: colors.textSecondary,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.65rem',
                  }}
                >
                  Other Tasks
                </Typography>
              </Box>
              <List disablePadding>
                {unassigned.map(renderStepRow)}
              </List>
            </Box>
          );
        })()}

        {/* Empty state */}
        {steps.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: colors.textSecondary }}>
            <Typography variant="body2">No steps in this plan yet.</Typography>
          </Box>
        )}

        {/* All steps pruned state */}
        {steps.length > 0 && visibleSteps.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4, color: colors.textSecondary }}>
            <Typography variant="body2">
              All {prunedStepCount} steps are pruned.
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: colors.textSecondary, cursor: 'pointer', textDecoration: 'underline' }}
              onClick={() => setShowPrunedSteps(true)}
            >
              Show pruned steps
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default StepListPane;
